import { atom } from 'jotai';

import type { ApprovalMode, AttachmentBlock } from '../../contracts/harness.types';
import type { RpaSegmentedTask } from '../../contracts/projects.types';
import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { WorkSurface } from '../../contracts/work-home-composer-analytics.types';
import type { Artifact } from './artifacts/state';

/**
 * Seamless first-turn hand-off for the Work-home landing composer.
 *
 * Mirrors how Evidence seeds a new chat: the opening message + all of
 * its context live in shared state that SURVIVES the route change —
 * never the URL. The landing flow creates the project, stamps this
 * slot with the project id + opening prompt + staged files +
 * the @-linked session/patient, then navigates to the clean project
 * URL (no ``?prompt`` / ``?link`` params).
 *
 * Two consumers read it on the project page (in either order, since the
 * chat is gated behind the page's project-loading spinner):
 *   1. ``ProjectChat`` uploads the staged ``files`` (→ ``attachment_ids``)
 *      and fires the opening turn; the per-project guard keeps it once.
 *   2. the Work chat route hydrates the canvas + composer chip from ``linked``.
 *   ``ProjectChat`` clears the prompt/files after consuming them, but
 *   preserves ``linked`` so the detail page can still hydrate context if
 *   its effect runs later.
 *
 * We stash the raw ``File`` objects (not uploaded IDs) so the upload
 * happens against the created project on the chat page, sharing the
 * exact same create → upload → submit-with-IDs path as a typed turn.
 *
 * Module-global (jotai default store) because the producer (landing)
 * unmounts during the route change before the consumers mount. The
 * ``projectId`` field guards against a stale slot being consumed by an
 * unrelated project navigation.
 */
export interface PendingFirstTurn {
  projectId: string;
  /** Wire prompt for the turn POST (may omit free-text already in ``computerUseSkill``). */
  prompt: string;
  /** User-visible slash-token prompt for the optimistic bubble when it differs from ``prompt``. */
  displayPrompt?: string;
  /** Hardcoded opening assistant turn to keep visible after project create. */
  openingAssistantMessage?: string;
  /** Whether the hardcoded opening assistant turn uses normal message actions. */
  openingAssistantMessageFeedback?: boolean;
  /**
   * A project created by the global New Chat action before Work mounted. Work
   * removes this marker, binds the project to its provisional chat, then
   * releases the create guard after the routed URL settles.
   */
  newChatNavigationHandoff?: { releaseGuard: () => void };
  /** Staged composer files when attachments are disabled client-side. */
  files: File[];
  /** Pre-uploaded chat attachment ids for the seeded first turn. */
  attachment_ids?: string[];
  /** Attachment blocks for the optimistic first-turn bubble. */
  optimisticAttachments?: AttachmentBlock[];
  /** Structured computer-use task compiled by the landing composer. */
  computerUseSkill?: RpaSegmentedTask;
  /** View-local choice captured before navigating to the newly created project. */
  desktopTargetOverride?: string;
  /** Origin analytics context retained until the accepted turn emit. */
  analytics?: {
    surface?: WorkSurface;
    work_mode?: string;
    has_slash_command?: boolean;
    entry_point?: string;
    clinical_session_id?: string;
  };
  /** Landing-composer pick, ridden on the seeded first turn's POST. */
  approvalMode?: ApprovalMode;
  /** Linked session / patient — hydrates the canvas + composer chip. */
  linked: LinkedContextItem | null;
  /** Canvas artifact open at create time — re-opened on the project page. */
  openArtifact?: Artifact | null;
}

export const pendingFirstTurnAtom = atom<PendingFirstTurn | null>(null);

/**
 * Project ids created from the Work home with a seeded opening turn that
 * hasn't produced its first transcript item yet.
 *
 * Module-level (not a jotai atom / component state) on purpose: it must
 * survive the client-side route change AND any remount of ``ProjectChat``
 * during the hand-off (the canvas host swaps the chat between full-width
 * and split layouts as the carried-over artifact hydrates, which remounts
 * the chat). While an id is in here, ``ProjectChat`` shows the "Heidi is
 * working" state instead of the cold "Automate your routine" empty card
 * — the seeded turn is moments from landing its optimistic bubble.
 *
 * Lifecycle: the landing flow adds the id right before navigating; the
 * chat removes it once the transcript has any item (or the seeded send
 * errors, so the empty state can return for a retry).
 */
export const seededFirstTurnProjectIds = new Set<string>();
